/**
 * What the two screens are allowed to say about coverage, decided once. F-129.
 *
 * **Its own module with no DOM and no React in it, for `plan/step.ts`'s reason**, and with more
 * riding on it than that one has. The screens this drives answer the owner's question —
 * *"I have 9 out of 12 researched and my plan is just 5 subjects"* — and the two ways of
 * answering it wrongly are both silent:
 *
 * - **Claiming a subject was dropped when nothing knows whether it was.** Two objectives are
 *   being taught from today whose plans were built before a node recorded which subject it came
 *   from. Counting their nodes by subject gives nought for every one of them, and a screen that
 *   rendered that would tell a learner in the middle of a course that their whole plan is
 *   missing. `inPlan` is `undefined` rather than `false` for exactly that case, so a component
 *   cannot accidentally render the claim: there is no sentence for "undefined" that says
 *   anything was lost.
 * - **Showing a subject as absent with no reason.** That is the bug `CoverageReport` was written
 *   to make impossible (`packages/shared/src/pipeline.ts`), and it comes back the moment a
 *   screen renders `why` without a fallback for the passes that left none. `whyKey` is never
 *   absent on a subject this module reports as absent, and the test asserts it for every
 *   outcome.
 *
 * The copy keys are returned rather than the words, because the words are in two languages and
 * a module a Node test can import cannot reach `i18n.ts` — it imports React. Returning the keys
 * as a **union of literals** rather than as `string` is what makes that split safe: `CopyKey` is
 * `keyof` the English dictionary, so a key this module invents that nobody wrote copy for fails
 * the web build instead of rendering its own name on a phone.
 */

/*
 * Types only, so nothing from `@nabu/shared` is in the bundle or in the Node test's runtime.
 * The names are the pipeline contract's own — `SubjectCoverage.why` is what the screen renders
 * and `why` is what the API sends — because a field renamed in the middle is a field two people
 * have to keep in step by memory.
 */
import type { CoverageReport, SubjectCoverage, SubjectOutcome } from '@nabu/shared';

/**
 * What `GET /api/coverage` answers with.
 *
 * `coverage` is the contract's report unchanged. The rest is what the browser needs in order to
 * know how much of it can be believed — see `joined`, which is the whole of the
 * plans-that-predate-this story.
 */
export interface CoverageView {
  coverage: CoverageReport;
  /** Subjects still being researched. They have no outcome yet and are not in `subjects`. */
  pending: number;
  /** Whether any node in the plan names the subject it came from. */
  joined: boolean;
  planNodes: number;
  /** Nodes naming no subject. Equal to `planNodes` for a plan built before F-129. */
  unattributed: number;
  /** `planIsThin` on the report, decided by the API so the two screens cannot disagree. */
  thin: boolean;
}

/**
 * The copy keys this module chooses between. Each one must exist in both dictionaries.
 *
 * **`cov.outcome.deep`, `cov.outcome.thin` and `cov.outcome.barren` were here until F-158.** They
 * were the outcome in the learner's language — *researched*, *little found*, *nothing found* —
 * and two screens wore them as a pill. They are a verdict on whether enough was found, the
 * threshold is entirely subject-dependent, and the product is not in a position to set one. The
 * `cov.why.*` sentences stay: saying *why* a subject is not in the plan is what this report is
 * for, and it is a statement about the plan rather than a grade on the subject.
 */
export type CoverageCopyKey = 'cov.why.deep' | 'cov.why.thin' | 'cov.why.barren';

/**
 * The two things that can actually help, and the reason there are only two.
 *
 * A subject with nothing behind it cannot be fixed by anything on our side: the material either
 * exists somewhere we did not look, in which case looking again with different angles is worth a
 * try, or it is on a page of a book the learner owns, in which case only they can supply it.
 * Anything else on that panel — a retry of the plan, a support address, an apology — offers
 * somebody a button that cannot change the answer.
 */
export type Remedy = 'upload' | 'again';

/** One subject, as the screens have to talk about it. */
export interface SubjectVerdict {
  subjectId: string;
  title: string;
  outcome: SubjectOutcome;
  /** How many topics of the plan came from this subject. Nought is legitimate and explained. */
  nodes: number;
  /**
   * Whether it reached the plan. **`undefined` means nothing knows**, which is the answer for a
   * researched subject on a plan built before nodes named theirs — never a reason to say
   * anything was lost.
   */
  inPlan: boolean | undefined;
  /** The pipeline's own words for why it is not in the plan. Empty when it left none. */
  why: string;
  /** The sentence to show when `why` is empty. Present whenever `inPlan` is `false`. */
  whyKey: CoverageCopyKey | undefined;
  /*
   * **`outcomeKey` was here until F-158.** It was the outcome as a word for a pill, and both
   * screens that wore it have stopped — see `CoverageCopyKey`. `outcome` itself stays, because
   * `remediesFor` branches on it and the counts below are built from it; what is gone is a word
   * for it on a learner's screen.
   */
  remedies: Remedy[];
}

/**
 * The sentence for a subject that is not in the plan and whose passes said nothing about it.
 *
 * It is a fallback and it is not decoration. The pipeline usually explains itself — the field
 * exists because a research pass once wrote five thousand words on why it could retrieve nothing
 * — but a row written by an older pass has no such field at all, and those are precisely the
 * objectives somebody is being taught from today. "Not in your plan", full stop, is the state
 * this feature was built to abolish, so there is a sentence for every outcome and none of them
 * is an apology for a fault of ours.
 */
const WHY_KEY: Record<SubjectOutcome, CoverageCopyKey> = {
  deep: 'cov.why.deep',
  thin: 'cov.why.thin',
  barren: 'cov.why.barren',
};

/**
 * What to offer for one subject.
 *
 * Nothing at all for a subject that is in the plan and was well researched: it is finished, and
 * a panel that offered two remedies under every one of twelve subjects would make the four that
 * need them impossible to pick out. A `thin` subject gets both even when it did reach the plan,
 * because "real material, incomplete" is exactly the state more material improves.
 */
function remediesFor(outcome: SubjectOutcome, inPlan: boolean | undefined): Remedy[] {
  if (outcome === 'deep' && inPlan !== false) return [];
  return ['upload', 'again'];
}

/**
 * Every subject with an outcome, in teaching order — which is the order the API sent them in.
 *
 * Not re-sorted here. The API sorts by the outline's `ordinal`, and a second sort in the browser
 * is a second opinion about the order of the same list, which is the disagreement this whole
 * feature exists to end rather than to reproduce one layer down.
 */
export function verdictsFor(view: CoverageView | undefined): SubjectVerdict[] {
  if (!view) return [];
  return view.coverage.subjects.map((subject) => verdictOf(subject, view.joined));
}

function verdictOf(subject: SubjectCoverage, joined: boolean): SubjectVerdict {
  /*
   * A `barren` subject is absent from the plan whatever the plan looks like — it never went to
   * synthesis — so that is knowable on an old plan as much as on a new one. Everything else
   * needs the join, and without it the answer is "nothing knows", not "no".
   */
  const known = joined || subject.outcome === 'barren';
  const inPlan = known ? subject.nodes > 0 : undefined;

  return {
    subjectId: subject.subjectId,
    title: subject.title,
    outcome: subject.outcome,
    nodes: subject.nodes,
    inPlan,
    why: subject.why,
    whyKey: inPlan === false ? WHY_KEY[subject.outcome] : undefined,
    remedies: remediesFor(subject.outcome, inPlan),
  };
}

/**
 * The verdicts by subject id, for the research screen — which lists subjects from the progress
 * route and needs to hang the outcome off each one.
 *
 * A subject in flight has no entry, which is correct: it has not been asked to reach the plan
 * yet, and the progress row already says what it is doing.
 */
export function verdictsById(view: CoverageView | undefined): Map<string, SubjectVerdict> {
  return new Map(verdictsFor(view).map((verdict) => [verdict.subjectId, verdict]));
}

/** The subjects that did not reach the plan, in teaching order. Never the unknown ones. */
export function missingFrom(view: CoverageView | undefined): SubjectVerdict[] {
  return verdictsFor(view).filter((verdict) => verdict.inPlan === false);
}

/**
 * The two numbers side by side, which is the whole of the owner's question.
 *
 * `known` is false when the plan cannot be matched to its subjects; the screen then says so
 * instead of showing a comparison one half of which is a guess.
 */
export interface Reconciliation {
  /** Subjects with an outcome. Not the same as "twelve", which includes those still running. */
  researched: number;
  inPlan: number;
  missing: number;
  pending: number;
  known: boolean;
}

export function reconcile(view: CoverageView | undefined): Reconciliation {
  if (!view) return { researched: 0, inPlan: 0, missing: 0, pending: 0, known: false };
  return {
    researched: view.coverage.subjects.length,
    inPlan: view.coverage.covered,
    missing: view.coverage.uncovered,
    pending: view.pending,
    known: view.joined,
  };
}

/**
 * What the plan screen says at the top of the plan, and nothing is the usual answer.
 *
 * - `thin` — the plan covers a fraction of what was researched. **Said out loud, honestly, and
 *   not as a failure**: a learner who asked about something obscure gets the little there is,
 *   with the gaps named and the offer to supply material. That is the product, not an error.
 * - `unattributed` — the plan predates the join. One sentence saying the two lists cannot be
 *   matched up here, which is better than a reconciliation that is quietly wrong and much better
 *   than a plan labelled "unknown subject" all the way down.
 * - `none` — either the plan accounts for what was researched, or there is no research to
 *   account for. An objective built from the learner's own pages has no subjects at all and must
 *   look like nothing happened.
 */
export type PlanNotice = 'thin' | 'unattributed' | 'none';

export function planNotice(view: CoverageView | undefined): PlanNotice {
  if (!view || view.planNodes === 0) return 'none';
  if (view.coverage.subjects.length === 0) return 'none';
  /*
   * Order matters here and it is worth the line. An unattributed plan can never be `thin` —
   * `planIsThin` needs `covered > 0` and nothing is covered when nothing is joined — so this
   * test cannot hide a thin plan, and putting it first keeps the impossible combination
   * ("a short plan; also we cannot tell what is in it") off the screen by construction.
   */
  if (!view.joined) return 'unattributed';
  return view.thin ? 'thin' : 'none';
}

/**
 * Which subject each part of the plan came from, for the chips on the plan screen.
 *
 * A map rather than a lookup through the report on every row, and it is empty for an
 * unattributed plan — a chip that said "unknown subject" on sixty-nine nodes would be a worse
 * answer than no chips at all, which is what an empty map produces.
 */
export function titlesBySubject(view: CoverageView | undefined): Map<string, string> {
  if (!view?.joined) return new Map();
  return new Map(
    view.coverage.subjects
      .filter((subject) => subject.title !== '')
      .map((subject) => [subject.subjectId, subject.title]),
  );
}
